import { formatCellValue, humanizeLabel, pnlColor } from "../../lib/format";
import { usePagedRows } from "../../lib/pagination";
import { CHART_INK, colorForSeries } from "../../lib/palette";
import { Pagination } from "./Pagination";

interface RecordListProps {
	columns: string[];
	rows: Record<string, unknown>[];
	pageSize?: number;
}

const IDENTITY_PATTERN = /profile|address/i;

// Many rows, each keyed by a wallet/profile identity plus a pile of metric
// columns -- shown as a grid of compact record cards instead of a wide,
// mostly-empty table that only a handful of columns actually need.
export function RecordList({ columns, rows, pageSize }: RecordListProps) {
	const { pageRows, page, pageCount, setPage } = usePagedRows(rows, pageSize);

	if (rows.length === 0) {
		return (
			<div className="text-xs font-mono text-base-content/50 py-6 text-center">
				No rows for this selection.
			</div>
		);
	}

	const identityColumns = columns.filter((col) => IDENTITY_PATTERN.test(col));
	const primaryIdentity = identityColumns[0] ?? columns[0] ?? "";
	const sideColumn = columns.find((col) => col === "side");
	const rankColumn = columns.find((col) => col === "rank");
	const headColumns = new Set(
		[primaryIdentity, sideColumn, rankColumn, ...identityColumns].filter(
			Boolean,
		) as string[],
	);
	const statColumns = columns.filter((col) => !headColumns.has(col));

	return (
		<div>
			<div className="grid gap-2 sm:grid-cols-2">
				{pageRows.map((row, i) => {
					const identity = formatCellValue(row[primaryIdentity]);
					const side = sideColumn ? String(row[sideColumn] ?? "") : null;
					const rank = rankColumn ? row[rankColumn] : null;

					return (
						<div
							key={i}
							className="rounded p-2.5"
							style={{
								border: `1px solid ${CHART_INK.gridline}`,
								background: "rgba(225,225,230,0.03)",
							}}
						>
							<div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-base-content/10">
								{rank !== null && rank !== undefined && (
									<span className="text-[10px] font-mono text-base-content/40 shrink-0">
										#{String(rank)}
									</span>
								)}
								{side && (
									<span
										className="inline-block w-2 h-2 rounded-sm shrink-0"
										style={{ backgroundColor: colorForSeries(side, 0) }}
									/>
								)}
								<span
									className="text-[11px] font-mono text-base-content font-semibold truncate"
									title={identity.full ?? undefined}
								>
									{identity.display}
								</span>
							</div>
							<div className="grid grid-cols-2 gap-x-3 gap-y-1">
								{statColumns.map((col) => {
									const cell = formatCellValue(row[col]);
									const color = pnlColor(col, row[col]);
									return (
										<div
											key={col}
											className="flex items-baseline justify-between gap-2 text-[10px] font-mono min-w-0"
										>
											<span className="text-base-content/45 truncate">
												{humanizeLabel(col)}
											</span>
											<span
												className="text-base-content/85 font-semibold tabular-nums truncate"
												title={cell.full ?? undefined}
												style={color ? { color } : undefined}
											>
												{cell.display}
											</span>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
			{pageSize && (
				<Pagination
					page={page}
					pageCount={pageCount}
					totalItems={rows.length}
					pageSize={pageSize}
					onPageChange={setPage}
				/>
			)}
		</div>
	);
}
